// KhoiHePage.tsx
import {
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from "@primer/octicons-react";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  PageLayout,
  TextInput,
  ThemeProvider,
  IconButton,
  Spinner,
  Text,
  ActionList,
} from "@primer/react";
import { khoiApi } from "../../api/khoiApi";
import { NotifyHelper } from "../../helpers/toast";
import { ComboboxTruong } from "../../components/combobox-truong";
import { Table } from "semantic-ui-react";
import { IHe } from "../../models/response/he/IHe";
import { heApi } from "../../api/heApi";
import { ListHeModal } from "./list_he_modal/list_he_modal";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";

interface IKhoi {
  id: number;
  ma_khoi: string;
  ten_khoi: string;
  ten_khoi_en: string;
  is_show_campaign: boolean;
  dm_truong_id: number;
}

const KhoiHePage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [dmTruongId, setDmTruongId] = useState<number>(0);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKhoi, setSelectedKhoi] = useState<IKhoi | null>(null);
  const [danhSachKhoi, setDanhSachKhoi] = useState<IKhoi[]>([]);
  const [listHe, setListHe] = useState<IHe[]>([]);
  const [selectedKhoiForHe, setSelectedKhoiForHe] = useState<IKhoi | null>(
    null
  );
  const [heByKhoi, setHeByKhoi] = useState<IHe[]>([]);
  const [isLoadingHe, setIsLoadingHe] = useState(false);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  useEffect(() => {
    if (dmTruongId) {
      loadDanhSachKhoi();
      getAllHe();
    }
  }, [dmTruongId]);

  useEffect(() => {
    if (selectedKhoiForHe?.id) {
      loadHeByKhoi(selectedKhoiForHe.id);
    }
  }, [selectedKhoiForHe]);

  const loadHeByKhoi = async (khoiId: number) => {
    try {
      setIsLoadingHe(true);
      const res = await khoiApi.selectHe(khoiId);
      if (res && res.is_success) {
        setHeByKhoi(Array.isArray(res.data) ? res.data : []);
      } else {
        setHeByKhoi([]);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi khi tải danh sách hệ");
      setHeByKhoi([]);
    } finally {
      setIsLoadingHe(false);
    }
  };

  const getAllHe = async () => {
    try {
      const res = await heApi.selectAll();
      if (res && res.is_success) {
        const filteredData = res.data.filter(
          (x: IHe) => x.dm_coso_id === dm_coso_id
        );
        setListHe(filteredData);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi khi tải danh sách hệ");
    }
  };

  const loadDanhSachKhoi = async () => {
    try {
      setIsLoading(true);
      const res = await khoiApi.selectAll();
      if (res && res.is_success) {
        const filteredData = res.data.filter(
          (x: IKhoi) => x.dm_truong_id === dmTruongId
        );
        setDanhSachKhoi(filteredData);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi tải danh sách khối");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (khoi: IKhoi) => {
    setSelectedKhoi(khoi);
    setShowModal(true);
  };

  const handleAdd = () => {
    setSelectedKhoi(null);
    setShowModal(true);
  };

  const handleDelete = (khoi: IKhoi) => {
    setSelectedKhoi(khoi);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!selectedKhoi) return;
    try {
      setIsLoading(true);
      const res = await khoiApi.delete(selectedKhoi.id);
      if (res.is_success) {
        NotifyHelper.Success("Xóa khối thành công");
        await loadDanhSachKhoi();
        setShowConfirmDelete(false);
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredData = danhSachKhoi.filter(
    (item) =>
      item.ten_khoi.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.ma_khoi.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box p={3}>
      {/* Header section */}
      <Box mb={3}>
        <Text as="h1" sx={{ fontSize: 20, fontWeight: "bold", mb: 2 }}>
          Khối - Hệ
        </Text>
        <Box display="flex" justifyContent="start" alignItems="center">
          <Box width={300}>
            <ComboboxTruong value={dmTruongId} onValueChanged={setDmTruongId} />
          </Box>
          <Box width={300} ml={1}>
            {" "}
            {/* Thêm margin left */}
            <TextInput
              width="100%"
              placeholder="Tìm kiếm khối..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leadingVisual={SearchIcon}
            />
          </Box>
        </Box>
      </Box>

      {/* Main content */}
      <Box display="flex" sx={{ height: "calc(100vh - 240px)" }}>
        {/* Left panel */}
        <Box flex={1} mr={3}>
          <Box
            mb={2}
            p={2}
            sx={{
              backgroundColor: "canvas.subtle",
              borderRadius: 1,
            }}
          >
            <Text sx={{ fontSize: 14, fontWeight: "bold" }}>
              Danh sách Khối ({filteredData.length})
            </Text>
          </Box>

          <Box sx={{ height: "calc(100% - 100px)", overflowY: "auto" }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <Spinner size="large" />
              </Box>
            ) : (
              <ActionList
                showDividers
                aria-label="Danh sách khối"
                selectionVariant="single"
                sx={{
                  backgroundColor: "canvas.default",
                  borderRadius: 1,
                  "& .ActionList-item": {
                    p: 2,
                  },
                }}
              >
                {filteredData.map((item: IKhoi) => (
                  <ActionList.Item
                    key={item.id}
                    selected={selectedKhoiForHe?.id === item.id}
                    onSelect={() => setSelectedKhoiForHe(item)}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "100px 200px 1fr",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Text sx={{ fontSize: 14 }}>{item.ma_khoi}</Text>
                      <Text sx={{ fontSize: 14 }}>{item.ten_khoi}</Text>
                      <Text sx={{ fontSize: 14, color: "fg.muted" }}>
                        {item.ten_khoi_en}
                      </Text>
                    </Box>
                  </ActionList.Item>
                ))}
              </ActionList>
            )}
          </Box>
        </Box>

        {/* Right panel */}
        <Box flex={1}>
          <Box
            mb={2}
            p={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              backgroundColor: "canvas.subtle",
              borderRadius: 1,
            }}
          >
            <Box>
              <Text sx={{ fontSize: 14, fontWeight: "bold", display: "block" }}>
                Danh sách Hệ
              </Text>
              {selectedKhoiForHe && (
                <Text sx={{ fontSize: 12, color: "fg.muted", mt: 1 }}>
                  Khối: {selectedKhoiForHe.ten_khoi}
                </Text>
              )}
            </Box>
            {selectedKhoiForHe && (
              <Button
                size="small"
                variant="primary"
                leadingVisual={PencilIcon}
                onClick={() => setShowModal(true)}
                sx={{
                  "&:hover": {
                    transform: "scale(1.02)",
                  },
                  transition: "transform 0.2s",
                }}
              >
                Quản lý Hệ
              </Button>
            )}
          </Box>

          <Box
            sx={{
              height: "calc(100% - 40px)",
              overflowY: "auto",
              backgroundColor: "canvas.default",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "border.default",
            }}
          >
            {isLoadingHe ? (
              <Box display="flex" justifyContent="center" p={3}>
                <Spinner size="large" />
              </Box>
            ) : heByKhoi.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                p={4}
              >
                <Text sx={{ color: "fg.muted", mb: 2 }}>
                  Chưa có Hệ nào được chọn
                </Text>
                {selectedKhoiForHe && (
                  <Button
                    size="small"
                    leadingVisual={PlusIcon}
                    onClick={() => setShowModal(true)}
                  >
                    Thêm Hệ mới
                  </Button>
                )}
              </Box>
            ) : (
              <ActionList
                showDividers
                aria-label="Danh sách hệ"
                selectionVariant="single"
              >
                {heByKhoi.map((he: IHe) => (
                  <ActionList.Item
                    key={he.id}
                    sx={{
                      p: 2,
                      "&:hover": {
                        backgroundColor: "canvas.subtle",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "100px 1fr",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Text sx={{ color: "fg.muted", fontSize: 13 }}>
                        {he.ma_he}
                      </Text>
                      <Text sx={{ fontWeight: "bold" }}>{he.ten_he}</Text>
                    </Box>
                  </ActionList.Item>
                ))}
              </ActionList>
            )}
          </Box>
        </Box>
      </Box>

      {/* Modals */}
      {showModal && (
        <ListHeModal
          dm_khoi_selected={selectedKhoiForHe?.id || 0}
          list_hes={listHe}
          listHeById={heByKhoi}
          onCancel={() => setShowModal(false)}
          onSuccess={(selectedHe) => {
            loadHeByKhoi(selectedKhoiForHe?.id || 0);
            setShowModal(false);
          }}
        />
      )}
    </Box>
  );
};

export default KhoiHePage;
